import { jsx as _jsx } from "react/jsx-runtime";
/**
 * StackFrame
 *
 * Rectilinear-polygon outline drawn around the stack tile + every
 * unfurled `BlockViewContainer` while the unfurl is at steady state
 * (`phase === "open"`). Renders as a single SVG `<path>` parented to
 * `.stream-group__cards` so it inherits the grid container's
 * coordinate system.
 *
 * The polygon's shape adapts to where the stack tile sits in the grid:
 *
 *   - L-shape when the tile is far-right and the unfurl spans most of
 *     a row underneath
 *   - Plain rectangle when the unfurl fits in a single row alongside
 *     the tile
 *   - Staircase when the tile is mid-row AND the last unfurl row is
 *     partial
 *   - Tall vertical strip on mobile (1-2 col grids)
 *
 * The trace is the same in every case: group in-stack item rects by
 * row top, reduce each row to `(top, bottom, leftmost.left,
 * rightmost.right)`, walk the perimeter clockwise emitting a vertex
 * at each row-edge transition, then convert vertices to a path with
 * quadratic-Bezier rounded corners.
 *
 * Within a row, in-stack items are guaranteed contiguous because the
 * Stream splices the unfurl as one continuous block of grid children
 * immediately after the stack tile (see `StreamGroup` in
 * `stream.tsx`, gated on `idx === stackIdxInGroup`). Any prior
 * row-mates of the stack tile get flex-wrap'd past the unfurl, which
 * is exactly the layout we want the polygon to outline.
 *
 * Visibility is gated on the `isVisible` prop (true when
 * `phase === "open"`). Fade in/out happens via a CSS class —
 * `useStackUnfurl`'s state machine is the source of truth, this
 * component just paints when told to.
 */
import { memo, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { getCollectionPaletteColor } from "@/config/block-styles";
import { ROW_EPSILON } from "./stack-layout";
// Corner rounding radius. Same value clamps automatically per-vertex
// when the adjacent edge is shorter than `2 * CORNER_RADIUS`.
const CORNER_RADIUS = 20;
// Distance the polygon sits from the items it encloses. Applied to
// every rect on all four sides before bucketing into rows, so the
// stroke stays visibly clear of card edges and adjacent rows'
// strokes don't visually touch at row boundaries (which was
// producing a "cross-over" artifact at the notch corners).
const FRAME_PADDING = 8;
function readLayoutRect(el) {
    const top = el.offsetTop;
    const left = el.offsetLeft;
    return {
        top,
        left,
        right: left + el.offsetWidth,
        bottom: top + el.offsetHeight,
    };
}
/**
 * Group raw rects into rows using `top` ± `ROW_EPSILON`. Each row's
 * `(top, bottom)` is the union of its members; `(left, right)` is the
 * extreme of its leftmost / rightmost member. Result is sorted top
 * ascending.
 *
 * `padding` inflates every rect's edges outward before bucketing —
 * the resulting row strips already include the desired gap between
 * the polygon and the items, so the trace doesn't need to know
 * about it. Within a row, every item shifts its `top` by the SAME
 * `-padding`, so row bucketing stays consistent after the inflation.
 */
function rectsToRows(rects, padding) {
    if (rects.length === 0)
        return [];
    const buckets = [];
    for (const rect of rects) {
        const top = rect.top - padding;
        const bottom = rect.bottom + padding;
        const left = rect.left - padding;
        const right = rect.right + padding;
        const existing = buckets.find((b) => Math.abs(b.top - top) <= ROW_EPSILON);
        if (existing === undefined) {
            buckets.push({ top, bottom, left, right });
            continue;
        }
        existing.top = Math.min(existing.top, top);
        existing.bottom = Math.max(existing.bottom, bottom);
        existing.left = Math.min(existing.left, left);
        existing.right = Math.max(existing.right, right);
    }
    buckets.sort((a, b) => a.top - b.top);
    return buckets;
}
/**
 * Walk the perimeter clockwise emitting a vertex at every 90-degree
 * turn. Straight-through transitions (where consecutive rows share a
 * left or right edge) are skipped so the rounded-corner pass doesn't
 * try to round a no-op vertex.
 *
 * Vertex order:
 *   1. Top-left of row 0
 *   2. Top-right of row 0
 *   3. Right-side step pairs, one for each `rows[i].right !==
 *      rows[i+1].right` transition
 *   4. Bottom-right of last row
 *   5. Bottom-left of last row
 *   6. Left-side step pairs, one for each `rows[i].left !==
 *      rows[i-1].left` transition (bottom-up iteration)
 */
function rowsToVertices(rows) {
    if (rows.length === 0)
        return [];
    const out = [];
    // Top-left + top-right of row 0
    out.push({ x: rows[0].left, y: rows[0].top });
    out.push({ x: rows[0].right, y: rows[0].top });
    // Right side, top-down. For each transition where the right edge
    // changes, emit two vertices at `y = cur.bottom`: one at the old
    // x (where the polygon turns horizontally) and one at the new x
    // (where it resumes vertical descent).
    for (let i = 0; i < rows.length - 1; i++) {
        const cur = rows[i];
        const nxt = rows[i + 1];
        if (cur.right === nxt.right)
            continue;
        out.push({ x: cur.right, y: cur.bottom });
        out.push({ x: nxt.right, y: cur.bottom });
    }
    // Bottom-right + bottom-left of last row
    const last = rows[rows.length - 1];
    out.push({ x: last.right, y: last.bottom });
    out.push({ x: last.left, y: last.bottom });
    // Left side, bottom-up. Mirror of the right-side step logic, this
    // time stepping at `y = cur.top`.
    for (let i = rows.length - 1; i > 0; i--) {
        const cur = rows[i];
        const nxt = rows[i - 1];
        if (cur.left === nxt.left)
            continue;
        out.push({ x: cur.left, y: cur.top });
        out.push({ x: nxt.left, y: cur.top });
    }
    return out;
}
/**
 * Convert a closed list of vertices into an SVG path string with
 * rounded corners. Each vertex becomes a short straight line
 * (entering) + a quadratic-Bezier curve (rounding the corner) +
 * another straight line (exiting). Per-vertex radius clamps to the
 * minimum of the two adjacent edge half-lengths so corners with
 * short edges don't overlap.
 *
 * Quadratic Bezier with the control point AT the vertex approximates
 * a quarter-circle for 90-degree corners — the same shape works for
 * both convex (outer) and concave (notch) corners; the curve always
 * sits "between" the incoming and outgoing edges.
 */
function tracePolygonRounded(vertices, radius) {
    const n = vertices.length;
    if (n < 3)
        return "";
    const inStops = new Array(n);
    const outStarts = new Array(n);
    for (let i = 0; i < n; i++) {
        const prev = vertices[(i - 1 + n) % n];
        const cur = vertices[i];
        const next = vertices[(i + 1) % n];
        const inDist = Math.hypot(cur.x - prev.x, cur.y - prev.y) || 1;
        const outDist = Math.hypot(next.x - cur.x, next.y - cur.y) || 1;
        const r = Math.min(radius, inDist / 2, outDist / 2);
        inStops[i] = {
            x: cur.x - (r * (cur.x - prev.x)) / inDist,
            y: cur.y - (r * (cur.y - prev.y)) / inDist,
        };
        outStarts[i] = {
            x: cur.x + (r * (next.x - cur.x)) / outDist,
            y: cur.y + (r * (next.y - cur.y)) / outDist,
        };
    }
    const cmds = [];
    cmds.push(`M ${inStops[0].x.toFixed(2)} ${inStops[0].y.toFixed(2)}`);
    for (let i = 0; i < n; i++) {
        const cur = vertices[i];
        const out = outStarts[i];
        cmds.push(`Q ${cur.x.toFixed(2)} ${cur.y.toFixed(2)} ${out.x.toFixed(2)} ${out.y.toFixed(2)}`);
        const nextIn = inStops[(i + 1) % n];
        cmds.push(`L ${nextIn.x.toFixed(2)} ${nextIn.y.toFixed(2)}`);
    }
    cmds.push("Z");
    return cmds.join(" ");
}
/**
 * Top-level orchestrator: read the in-stack rects (stack tile + each
 * unfurled item) via `offset*` properties — which are LAYOUT
 * positions (no transforms applied) — bucket them into rows, trace
 * the rectilinear polygon, round the corners, and return the SVG
 * path `d` string.
 *
 * IMPORTANT: This function MUST read layout positions, not visual /
 * transformed ones. The open and close FLIPs apply temporary
 * `transform: translate(...)` to the in-stack items while they
 * animate. If we used `getBoundingClientRect()`, any recompute
 * triggered mid-FLIP (e.g., a ResizeObserver firing because the
 * cards container grew when items mounted) would read the
 * transformed positions and produce a wrong polygon — the user
 * would see the frame "flash" at the right shape and then collapse
 * to wherever the items are mid-animation.
 *
 * `offsetTop` / `offsetLeft` / `offsetWidth` / `offsetHeight` ignore
 * transforms entirely, so the polygon is always anchored to the
 * items' final layout positions regardless of whether a FLIP is in
 * flight.
 *
 * Returns `undefined` when there's nothing to draw (no items
 * registered yet, or fewer than 3 vertices — which can't happen for
 * a non-empty unfurl but is a defensive guard).
 */
export function computeStackFrameGeometry(tile, unfurlItems, radius = CORNER_RADIUS, padding = FRAME_PADDING) {
    const tileLayout = readLayoutRect(tile);
    const itemLayouts = unfurlItems.map(readLayoutRect);
    const rows = rectsToRows([tileLayout, ...itemLayouts], padding);
    if (rows.length === 0)
        return undefined;
    const vertices = rowsToVertices(rows);
    if (vertices.length < 3)
        return undefined;
    return tracePolygonRounded(vertices, radius);
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const StackFrame = memo(function StackFrame({ stackId, paletteId, cardsRoot, isVisible, unfurlItemCount, }) {
    const [pathD, setPathD] = useState(undefined);
    // `isShowing` lags one rAF behind `isVisible` on ENTRY so the
    // browser commits the invisible SVG before `--visible` is applied
    // and the CSS fade transition fires. On EXIT we clear it immediately
    // so the fade-out starts at once.
    const [isShowing, setIsShowing] = useState(false);
    const hasPath = pathD !== undefined;
    useEffect(() => {
        if (!isVisible || !hasPath) {
            setIsShowing(false);
            return;
        }
        const raf = requestAnimationFrame(() => setIsShowing(true));
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, hasPath]);
    useLayoutEffect(() => {
        // Skip work entirely when the frame isn't supposed to be visible.
        // The FLIP is mid-flight in `opening` / `closing`, so any path we
        // compute during those phases would be stale by the next frame.
        if (!isVisible)
            return;
        const recompute = () => {
            const tile = cardsRoot.querySelector(`[data-stack-id="${CSS.escape(stackId)}"]`);
            if (tile === null)
                return;
            const items = Array.from(cardsRoot.querySelectorAll('[data-unfurl-item="true"]'));
            setPathD(computeStackFrameGeometry(tile, items));
        };
        recompute();
        // Re-trace whenever the grid container or any in-stack item
        // resizes. Image lazy-loads, viewport resizes, font swaps all
        // flow through this. `requestAnimationFrame` coalescing limits
        // recomputation to one per frame even on bursty observer fires.
        let frameHandle;
        const scheduleRecompute = () => {
            if (frameHandle !== undefined)
                cancelAnimationFrame(frameHandle);
            frameHandle = requestAnimationFrame(recompute);
        };
        const ro = new ResizeObserver(scheduleRecompute);
        ro.observe(cardsRoot);
        const tile = cardsRoot.querySelector(`[data-stack-id="${CSS.escape(stackId)}"]`);
        if (tile !== null)
            ro.observe(tile);
        for (const el of cardsRoot.querySelectorAll('[data-unfurl-item="true"]')) {
            ro.observe(el);
        }
        // Re-trace whenever the grid's direct children change — covers the
        // case where the open stack's `updatedAt` bumps (e.g. a member was
        // edited or dragged in) and the Stream re-orders `group.items`,
        // moving the stack tile and its spliced-in unfurl items earlier
        // in the cards container. The tile's `offsetTop` / `offsetLeft`
        // have changed but no element has been resized, so the
        // ResizeObserver above never fires. React's reconciler reorders
        // children with `insertBefore` / `removeChild` to match the new
        // key order, which delivers a `childList` mutation here and lets
        // us re-anchor the polygon to the tile's new layout position
        // within the same frame. Also catches sibling adds / removes that
        // change a NON-stack item's count (e.g. a fresh capture lands in
        // a higher group and pushes everything below down) — those don't
        // round-trip through `unfurlItemCount`, but they do shift the
        // tile, and we want the frame to follow.
        //
        // `childList: true` (no `subtree`) is enough: only direct grid
        // children participate in the CSS grid layout that determines the
        // tile's position, so we don't need to chase mutations inside
        // those children. Image lazy-loads / textareas growing inside a
        // sibling still flow through the existing ResizeObserver (they
        // change the sibling's height, which changes the grid container's
        // height, which fires the cardsRoot resize).
        const mo = new MutationObserver(scheduleRecompute);
        mo.observe(cardsRoot, { childList: true });
        return () => {
            ro.disconnect();
            mo.disconnect();
            if (frameHandle !== undefined)
                cancelAnimationFrame(frameHandle);
        };
        // `unfurlItemCount` is in deps so a membership change (omnibar add,
        // drag-in, drag-out, delete) tears down the stale observer set and
        // rebuilds it against the new DOM — the recompute at the top of
        // the effect picks up the new items via querySelectorAll and the
        // fresh `ro.observe(...)` loop hooks resize tracking onto each
        // one. See the prop doc for why ResizeObserver alone misses this.
    }, [stackId, cardsRoot, isVisible, unfurlItemCount]);
    // Per-stack hue: deterministic mapping from `paletteId` (or
    // `stackId` as a fallback) into the shared post-it palette.
    // Synthetic stacks pass their PARENT collection id so the frame
    // shares a hue with the closed-state banner (which is keyed off the
    // same parent palette); real Stacks omit `paletteId` and fall back
    // to their own id, where the two are the same value anyway.
    const colorPaletteId = paletteId ?? stackId;
    const colors = useMemo(() => getCollectionPaletteColor(colorPaletteId), [colorPaletteId]);
    if (pathD === undefined)
        return null;
    const className = ["stack-frame", isShowing ? "stack-frame--visible" : ""]
        .filter(Boolean)
        .join(" ");
    return (_jsx("svg", { className: className, width: "100%", height: "100%", "aria-hidden": true, "data-stack-frame": "true", children: _jsx("path", { d: pathD, stroke: colors.notch, strokeWidth: 2, fill: `color-mix(in srgb, ${colors.notch} 8%, white)`, strokeLinejoin: "round" }) }));
});
